/* ============================================================
   GoodBuyz variant bottom-sheet
   ============================================================
   Wires the Apple-style option sheet (snippets/gb-variant-sheet.liquid) to the
   theme's existing <variant-radios> picker.

   The radios stay the source of truth: selecting a value here just checks the
   matching radio and dispatches a bubbling `change`, which the <variant-radios>
   custom element (assets/variants.js) already listens for — so price, media,
   URL and the add-to-cart form all update through the theme's own code path.
   Nothing here touches variant availability or pricing.

   Scope: every lookup is confined to the sheet's own `.productView-options`
   container, so a product page with a sticky bar (its own
   <variant-sticky-selects>) is never cross-wired.
   ============================================================ */
(function () {
  'use strict';

  var OPEN_CLASS = 'is-open';
  var openSheet = null;
  var lastFocus = null;

  function optionsContainer(node) {
    return node.closest('.productView-options') || document;
  }

  // Attribute selector value has to be quoted because option names can contain
  // spaces or punctuation; JSON.stringify gives a safe double-quoted string.
  function radiosFor(container, optionName) {
    return container.querySelectorAll(
      'input.product-form__radio[name=' + JSON.stringify(optionName) + ']'
    );
  }

  function lockScroll(lock) {
    document.documentElement.style.overflow = lock ? 'hidden' : '';
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  function open(sheet, trigger) {
    if (openSheet) close(openSheet);
    lastFocus = trigger || document.activeElement;
    sheet.hidden = false;
    // Force a reflow so the transform transition runs from its hidden state.
    void sheet.offsetWidth;
    sheet.classList.add(OPEN_CLASS);
    lockScroll(true);
    openSheet = sheet;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    var selected = sheet.querySelector('.gb-vsheet__option.is-selected') ||
      sheet.querySelector('.gb-vsheet__option');
    if (selected) selected.focus({ preventScroll: true });
  }

  function close(sheet) {
    if (!sheet) return;
    sheet.classList.remove(OPEN_CLASS);
    lockScroll(false);
    var trigger = document.querySelector('[data-gb-vsheet-open="' + sheet.id + '"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    var done = function () {
      sheet.hidden = true;
      sheet.removeEventListener('transitionend', done);
    };
    // Fall back to an immediate hide if the transition never fires.
    sheet.addEventListener('transitionend', done);
    setTimeout(done, 400);
    if (openSheet === sheet) openSheet = null;
    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus({ preventScroll: true });
    }
    lastFocus = null;
  }

  // Reflect the currently-checked radios into a group's triggers and rows, so
  // the sheet stays in step with any selection made elsewhere (e.g. an inline
  // swatch on a wider screen, or variants.js resetting an unavailable option).
  function sync(group) {
    var container = optionsContainer(group);
    group.querySelectorAll('.gb-vsheet-trigger').forEach(function (trigger) {
      var valueEl = trigger.querySelector('[data-gb-vsheet-current]');
      if (!valueEl) return;
      var name = valueEl.getAttribute('data-gb-vsheet-current');
      var checked = container.querySelector(
        'input.product-form__radio[name=' + JSON.stringify(name) + ']:checked'
      );
      if (checked) valueEl.textContent = checked.value;
    });
    group.querySelectorAll('.gb-vsheet').forEach(function (sheet) {
      var name = sheet.getAttribute('data-option-name');
      var checked = container.querySelector(
        'input.product-form__radio[name=' + JSON.stringify(name) + ']:checked'
      );
      var current = checked ? checked.value : null;
      sheet.querySelectorAll('.gb-vsheet__option').forEach(function (opt) {
        var isSel = opt.getAttribute('data-option-value') === current;
        opt.classList.toggle('is-selected', isSel);
        opt.setAttribute('aria-pressed', isSel ? 'true' : 'false');
      });
    });
  }

  // Sticky-bar variant buttons (snippets/gb-sticky-variant-triggers.liquid) show
  // the option NAME by default. We keep aria-label in step with the checked radio
  // on every sync; the VISIBLE label is only swapped to the value once the shopper
  // actually picks that option (see updateStickyLabel, called on change).
  function syncSticky() {
    document.querySelectorAll('[data-gb-sticky-variants]').forEach(function (bar) {
      bar.querySelectorAll('.gb-sticky-variant-btn').forEach(function (btn) {
        var name = btn.getAttribute('data-gb-sticky-option');
        if (!name) return;
        var checked = document.querySelector(
          '.productView-options input.product-form__radio[name=' + JSON.stringify(name) + ']:checked'
        );
        if (!checked) return;
        btn.setAttribute('aria-label', name + ': ' + checked.value);
      });
    });
  }

  // Swap the visible label of the sticky button for ONE option to the chosen
  // value. Called when a variant actually changes, so the button reads "Crimson"
  // after a pick while the others keep their name ("Size") until picked.
  function updateStickyLabel(optionName, value) {
    document.querySelectorAll('[data-gb-sticky-variants] .gb-sticky-variant-btn').forEach(function (btn) {
      if (btn.getAttribute('data-gb-sticky-option') !== optionName) return;
      var label = btn.querySelector('[data-gb-sticky-label]');
      if (label) label.textContent = value;
      btn.setAttribute('aria-label', optionName + ': ' + value);
    });
  }

  function selectValue(sheet, optionName, value) {
    var container = optionsContainer(sheet);
    var radios = radiosFor(container, optionName);
    var target = null;
    radios.forEach(function (radio) {
      var match = radio.value === value;
      radio.checked = match;
      if (match) target = radio;
    });
    if (target) {
      // Bubbles up to <variant-radios>, which owns the rest of the update.
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-gb-vsheet-open]');
    if (opener) {
      var sheet = document.getElementById(opener.getAttribute('data-gb-vsheet-open'));
      if (sheet) {
        event.preventDefault();
        open(sheet, opener);
      }
      return;
    }

    if (event.target.closest('[data-gb-vsheet-close]')) {
      event.preventDefault();
      close(openSheet);
      return;
    }

    // "See more" reveals the rows held back past the 9-value cap (dev note).
    var more = event.target.closest('[data-gb-vsheet-more]');
    if (more) {
      event.preventDefault();
      var list = document.getElementById(more.getAttribute('data-gb-vsheet-more'));
      if (list) list.classList.remove('is-collapsed');
      more.hidden = true;
      return;
    }

    var optionBtn = event.target.closest('[data-gb-vsheet-select]');
    if (optionBtn) {
      event.preventDefault();
      var sheetEl = optionBtn.closest('.gb-vsheet');
      selectValue(
        sheetEl,
        optionBtn.getAttribute('data-option-name'),
        optionBtn.getAttribute('data-option-value')
      );
      // sync runs off the radio's own change event below; close after it.
      close(sheetEl);
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && openSheet) close(openSheet);
  });

  // Any variant change (from the sheet or from an inline picker) re-syncs the
  // groups. Delegated on the document so it survives section re-renders.
  document.addEventListener('change', function (event) {
    if (!event.target.classList || !event.target.classList.contains('product-form__radio')) return;
    var container = event.target.closest('.productView-options');
    if (!container) return;
    container.querySelectorAll('[data-gb-vsheet-group]').forEach(sync);
    syncSticky();
    // The option the shopper just changed now shows its value on the sticky bar.
    updateStickyLabel(event.target.name, event.target.value);
  });

  function init() {
    document.querySelectorAll('[data-gb-vsheet-group]').forEach(sync);
    syncSticky();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
